import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { useDispatch } from 'react-redux';
import { setLoader, setErrorAlert, setSuccessAlert } from '../../store/alert/alertSlice';
import { postDataAPI } from '../../utils/fetchData';
import SpotifyLogo from '../../svg/Social/Spotify';

const SpotifyConnector = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [spotifyLink, setSpotifyLink] = useState("");
  const [isValidLink, setIsValidLink] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [discoveredLinks, setDiscoveredLinks] = useState(null);
  const [profileList, setProfileList] = useState(null);
  const [linksFound, setLinksFound] = useState(0);
  const [artistName, setArtistName] = useState("");
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    // Check if user already has Spotify connected
    checkExistingConnection();
  }, []);

  const checkExistingConnection = async () => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const user = JSON.parse(localStorage.getItem('dizeeUser') || '{}');
      
      // Check if user has Spotify link in their profile
      if (user?.social?.sf || user?.spotify) {
        setIsConnected(true);
        setSpotifyLink(user.social?.sf || user.spotify || '');
        setIsValidLink(true);
      }
    } catch (error) {
      console.error('Error checking existing connection:', error);
    }
  };

  // Validate Spotify link
  const validateSpotifyLink = (link) => {
    if (!link) return false;
    try {
      const url = new URL(link);
      return url.hostname.includes('spotify.com');
    } catch {
      return false;
    }
  };

  // Handle link input change
  const handleLinkChange = async (e) => {
    const value = e.target.value;
    setSpotifyLink(value);
    setIsValidLink(validateSpotifyLink(value));

    // If not a valid link, search for artists by name
    if (!validateSpotifyLink(value) && value.length > 2) {
      try {
        const token = JSON.parse(localStorage.getItem('dizeeToken'));
        const response = await postDataAPI("user/discoverLinksFromArtist", { artistName: value }, token);
        if (response.data && response.data.data) {
          setProfileList(response.data.data.response || []);
        }
      } catch (error) {
        console.error('Error searching artists:', error);
        setProfileList([]);
      }
    } else {
      setProfileList(null);
    }
  };

  // Process with AI to discover links and trigger catalog import
  const discoverLinks = async () => {
    if (!isValidLink) return;

    setIsProcessing(true);
    dispatch(setLoader(true));

    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      console.log("Sending request to discover links for:", spotifyLink);
      const response = await postDataAPI("user/discoverLinksWithAI", { spotifyUrl: spotifyLink }, token);

      console.log("Received response:", response.data);

      if (response.data?.status === 200) {
        const links = response.data?.data?.response;
        console.log("Discovered links:", links);

        // Save all links to state
        setDiscoveredLinks({
          spotify: links?.spotify || spotifyLink,
          instagram: links?.instagram || "",
          tiktok: links?.tiktok || "",
          twitter: links?.twitter || "",
          facebook: links?.facebook || "",
          youtube: links?.youtube || "",
          soundcloud: links?.soundcloud || "",
          appleMusic: links?.appleMusic || "",
          topSongs: links?.topSongs || [],
          topVideo: links?.topVideo || "",
          events: links?.events || []
        });

        // Calculate number of links found
        const validLinkCount = Object.entries(links)
          .filter(([key, value]) =>
            key !== 'artistName' &&
            key !== 'topSongs' &&
            key !== 'events' &&
            key !== 'profileImage' &&
            value &&
            (typeof value === 'string' ? value.trim() !== '' : value.length > 0)
          ).length;

        setLinksFound(validLinkCount);
        setArtistName(links?.artistName || "your artist");

        // Update user profile with discovered links
        try {
          const updateResponse = await postDataAPI("user/update-user-article", {
            social: {
              sf: links?.spotify || spotifyLink,
              ig: links?.instagram || "",
              tt: links?.tiktok || "",
              x: links?.twitter || "",
              fb: links?.facebook || "",
              yt: links?.youtube || "",
              sc: links?.soundcloud || "",
              am: links?.appleMusic || "",
            },
            name: links?.artistName,
            avatar: links?.profileImage
          }, token);

          // Update localStorage user data
          const user = JSON.parse(localStorage.getItem('dizeeUser') || '{}');
          const updatedUser = {
            ...user,
            name: links?.artistName || user.name,
            avatar: links?.profileImage || user.avatar,
            social: {
              ...user?.social,
              sf: links?.spotify || spotifyLink,
              ig: links?.instagram || user?.social?.ig || "",
              tt: links?.tiktok || user?.social?.tt || "",
              x: links?.twitter || user?.social?.x || "",
              fb: links?.facebook || user?.social?.fb || "",
              yt: links?.youtube || user?.social?.yt || "",
              sc: links?.soundcloud || user?.social?.sc || "",
              am: links?.appleMusic || user?.social?.am || "",
            }
          };
          localStorage.setItem('dizeeUser', JSON.stringify(updatedUser));
        } catch (error) {
          console.error('Error updating user profile:', error);
        }

        // Trigger catalog import if artist name is available
        if (links?.artistName) {
          try {
            console.log('🎵 Triggering catalog import for:', links.artistName);
            const catalogResponse = await postDataAPI("user/discoverCatalogFromArtist", {
              artistName: links.artistName,
              spotifyUrl: links?.spotify || spotifyLink
            }, token);

            if (catalogResponse.data?.success) {
              console.log('✅ Catalog import triggered successfully');
              dispatch(setSuccessAlert(`Connected to Spotify! Catalog import started for ${links.artistName}`));
            }
          } catch (error) {
            console.error('Error triggering catalog import:', error);
            // Don't fail the whole flow if catalog import fails
            dispatch(setSuccessAlert(`Connected to Spotify! Found ${validLinkCount} links for ${links.artistName}`));
          }
        } else {
          dispatch(setSuccessAlert(`Found ${validLinkCount} links for your artist profile`));
        }

        setIsConnected(true);
        
        // Update connector status
        try {
          const connectorStatuses = JSON.parse(localStorage.getItem('connectorStatuses') || '{}');
          connectorStatuses.spotify = true;
          localStorage.setItem('connectorStatuses', JSON.stringify(connectorStatuses));
        } catch (e) {
          console.error('Error saving connection status:', e);
        }

      } else {
        throw new Error(response.data?.message || "Couldn't find related links. Please try a different Spotify URL.");
      }
    } catch (error) {
      console.error("Error discovering links:", error);
      dispatch(setErrorAlert(error?.response?.data?.message || "Error connecting to service. Please try again."));
    } finally {
      setIsProcessing(false);
      dispatch(setLoader(false));
    }
  };

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Spotify</span>
            <span 
              className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
              onClick={() => navigate(-1)}
            >
              Go back
            </span>
          </div>

          <div className="w-full px-4 pb-20 overflow-y-auto flex-1">
            {/* Description */}
            <div className="mb-8">
              <p className="text-white text-[12px] opacity-70 leading-relaxed">
                Connect your Spotify artist profile to sync your music catalog, discover your online presence, 
                and automatically import your releases.
              </p>
            </div>

            {/* Connection Status */}
            {isConnected && discoveredLinks && (
              <div className="mb-6 bg-black border border-green-500 border-opacity-30 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center border border-[rgba(255,255,255,0.1)]"
                      style={{ backgroundColor: 'rgba(29, 185, 84, 0.2)' }}
                    >
                      <SpotifyLogo className="w-6 h-6" style={{ fill: '#1DB954' }} />
                    </div>
                    <div>
                      <p className="text-white text-[12px] font-medium mb-1">{artistName}</p>
                      <p className="text-white text-[11px] opacity-50">
                        {linksFound} links found • Catalog importing
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 bg-green-500 bg-opacity-20 border border-green-500 border-opacity-30 rounded-full px-3 py-1.5">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-green-500 text-[10px] font-medium">Connected</span>
                  </div>
                </div>
              </div>
            )}

            {/* Search/Connect Section */}
            {!isConnected && (
              <div className="mb-6 bg-black border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <SpotifyLogo className="w-5 h-5" style={{ fill: '#1DB954' }} />
                    <p className="text-white text-[12px] font-medium">Search for your artist profile</p>
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      value={spotifyLink}
                      onChange={handleLinkChange}
                      placeholder="Enter Spotify artist link or search by name"
                      className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-3 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none focus:border-[#1DB954] transition-all"
                    />
                    
                    {/* Search Results Dropdown */}
                    {profileList && profileList.length > 0 && (
                      <div className="absolute z-10 w-full mt-2 bg-black border border-[rgba(255,255,255,0.1)] rounded-lg overflow-hidden">
                        <div className="max-h-[200px] overflow-y-auto">
                          {profileList.map((profile, index) => (
                            <div
                              key={index}
                              onClick={() => {
                                setSpotifyLink(profile.spotifyUrl);
                                setIsValidLink(validateSpotifyLink(profile.spotifyUrl));
                                setProfileList(null);
                              }}
                              className="flex items-center gap-3 px-4 py-3 hover:bg-[rgba(255,255,255,0.05)] cursor-pointer transition-all"
                            >
                              <img 
                                src={profile.image} 
                                alt={profile.name}
                                className="w-8 h-8 rounded-full object-cover"
                                onError={(e) => {
                                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="%231DB954"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z"/></svg>';
                                }}
                              />
                              <span className="text-white text-[12px]">{profile.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Discover Button */}
                {isValidLink && !isProcessing && (
                  <button
                    onClick={discoverLinks}
                    className="w-full bg-[#1DB954] text-white text-[12px] font-semibold py-3 rounded-lg hover:opacity-90 transition-all"
                  >
                    Connect & Import Catalog
                  </button>
                )}

                {/* Processing State */}
                {isProcessing && (
                  <div className="flex items-center justify-center gap-2 py-3">
                    <div className="w-4 h-4 border-2 border-[#1DB954] border-t-transparent rounded-full animate-spin" />
                    <span className="text-white text-[12px] opacity-70">
                      Discovering links and importing catalog...
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Success Message */}
            {discoveredLinks && !isProcessing && (
              <div className="mb-6 bg-green-500 bg-opacity-10 border border-green-500 border-opacity-20 rounded-lg p-4">
                <p className="text-green-400 text-[12px] mb-2">
                  ✅ Found {linksFound} links for {artistName}
                </p>
                {discoveredLinks.topSongs && discoveredLinks.topSongs.length > 0 && (
                  <p className="text-white text-[11px] opacity-70">
                    Including {discoveredLinks.topSongs.length} top songs • Catalog import in progress
                  </p>
                )}
              </div>
            )}

            {/* Features */}
            <div className="space-y-3">
              <div className="bg-black border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                <p className="text-white text-[11px] opacity-70 mb-2">
                  <span className="font-semibold text-white">What happens when you connect:</span>
                </p>
                <ul className="text-white text-[11px] opacity-70 space-y-1 list-disc list-inside">
                  <li>Your music catalog is automatically imported</li>
                  <li>Social media profiles are discovered and linked</li>
                  <li>Top songs and releases are synced</li>
                  <li>Analytics and playlist data become available</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
};

export default SpotifyConnector;

